/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {FormLabel} from '@dropbox/dig-components/form_row';
import {Modal} from '@dropbox/dig-components/modal';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import {type AdminHomeBanner, AdminHomeBannersService, ApiError} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {Title} from 'components/DSYS/Title';
import {useSetAtom} from 'jotai';
import {ReactNode, useEffect, useRef, useState} from 'react';
import {getService} from 'utilities';
import {suggestDarkColor, suggestLightColor} from 'views/home/bannerColors';

import {BannerColorsField} from './BannerColorsField';
import {BannerCopyFields} from './BannerCopyFields';
import {
  BannerDraft,
  draftFromBanner,
  ImageSlot,
  newBannerDraft,
  toCreateBody,
  toUpdateBody,
  validateDraft,
} from './bannerForm';
import {BannerImagePicker, imageSlotSrc} from './BannerImagePicker';
import {extractPalette, MIN_IMAGE_SIZE, SHARPEST_IMAGE_SIZE} from './bannerImages';
import {BannerPreview, HOME_BANNER_WIDTH} from './BannerPreview';
import {bannerStatus} from './bannerSchedule';
import {BannerScheduleField} from './BannerScheduleField';
import styles from './HomeBanners.module.css';
import {useImagePalette} from './useImagePalette';

export const ADMIN_HOME_BANNERS_QUERY_KEY = ['admin', 'home-banners'] as const;

// Wide enough for the preview to show the banner at home's full width: the
// banner, the preview stage's padding and border, and the modal's own padding.
const MODAL_WIDTH = HOME_BANNER_WIDTH + 2 * 16 + 2 + 2 * 24;

const service = () => getService(AdminHomeBannersService);

/** The API's message for a failed save: a string for most errors, a list for invalid fields. */
export const saveErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    const detail = (error.body as {detail?: unknown} | undefined)?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map((item: {msg?: string}) => (item.msg ?? '').replace(/^Value error, /, ''))
        .filter(Boolean)
        .join(' ');
    }
    if (error.status === 403) {
      return "You don't have access to manage home banners.";
    }
  }
  return "The banner couldn't be saved. Your changes are still here; try again.";
};

const Section = ({title, children}: {title: string; children: ReactNode}) => (
  <Stack gap="12">
    <Title size={16}>{title}</Title>
    {children}
  </Stack>
);

/**
 * Create a banner, or edit one (live ones included). Nothing reaches the server,
 * images included, until Create or Save; Cancel leaves no trace.
 *
 * Keep one instance mounted and change `open`; change `session` for a fresh
 * form. react-modal only unhides the app reliably when an open modal closes:
 * mounting or unmounting one while open (in StrictMode) leaves the whole app
 * aria-hidden.
 */
export const BannerEditorModal = ({
  open,
  session,
  banner,
  onClose,
}: {
  open: boolean;
  session: number;
  /** The banner to edit, or null to create one. */
  banner: AdminHomeBanner | null;
  onClose: () => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Modal
      open={open}
      width={MODAL_WIDTH}
      withCloseButton="Close"
      aria-labelledby="banner-editor-title"
      onRequestClose={() => !isSaving && onClose()}
    >
      <BannerEditor key={session} banner={banner} onClose={onClose} onSavingChange={setIsSaving} />
    </Modal>
  );
};

const BannerEditor = ({
  banner,
  onClose,
  onSavingChange,
}: {
  banner: AdminHomeBanner | null;
  onClose: () => void;
  onSavingChange: (isSaving: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const [draft, setDraft] = useState<BannerDraft>(() =>
    banner ? draftFromBanner(banner) : newBannerDraft()
  );
  const [showErrors, setShowErrors] = useState(false);
  const errors = validateDraft(draft);
  const palette = useImagePalette(draft.image);
  const darkPalette = useImagePalette(draft.dark_image);

  // Revoke the local previews of picked files when they're replaced or the modal closes.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(
    () => () => {
      for (const slot of [draftRef.current.image, draftRef.current.dark_image]) {
        if (slot.kind === 'new') {
          URL.revokeObjectURL(slot.previewUrl);
        }
      }
    },
    []
  );

  const update = (patch: Partial<BannerDraft>) => setDraft((prev) => ({...prev, ...patch}));

  const setImage = async (field: 'image' | 'dark_image', slot: ImageSlot) => {
    const previous = draft[field];
    if (previous.kind === 'new' && previous !== slot) {
      URL.revokeObjectURL(previous.previewUrl);
    }
    update({[field]: slot});
    if (slot.kind !== 'new') {
      return;
    }
    // A new image re-suggests the colors it drives; admins can fine-tune after.
    const [color] = await extractPalette(slot.previewUrl).catch(() => [] as string[]);
    // Skip it if the image was replaced or removed while its palette loaded.
    if (!color || draftRef.current[field] !== slot) {
      return;
    }
    setDraft((prev) =>
      field === 'image'
        ? {
            ...prev,
            light_background_color: suggestLightColor(color),
            // A dark image, if there is one, decides the dark color instead.
            ...(prev.dark_image.kind === 'none'
              ? {dark_background_color: suggestDarkColor(color)}
              : {}),
          }
        : {...prev, dark_background_color: suggestDarkColor(color)}
    );
  };

  const save = useMutation({
    mutationFn: (current: BannerDraft) =>
      banner
        ? service().updateHomeBannerApiV1AdminHomeBannersBannerIdPut(
            banner.id,
            toUpdateBody(current)
          )
        : service().createHomeBannerApiV1AdminHomeBannersPost(toCreateBody(current)),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ADMIN_HOME_BANNERS_QUERY_KEY});
      setSnackbar({text: banner ? 'Banner saved.' : 'Banner created.'});
      onClose();
    },
  });

  const submit = () => {
    setShowErrors(true);
    if (Object.keys(errors).length === 0) {
      save.mutate(draft);
    }
  };

  const shownErrors = showErrors ? errors : {};
  const isLive = banner !== null && bannerStatus(banner) === 'live';
  const title = banner ? 'Edit banner' : 'New banner';

  useEffect(() => onSavingChange(save.isPending), [save.isPending, onSavingChange]);

  return (
    <>
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title id="banner-editor-title" weightVariant="emphasized">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="24">
          {isLive && (
            <SystemBanner tone="attention">
              <Text>This banner is live. Saved changes show on home after the next page load.</Text>
            </SystemBanner>
          )}

          {/* Stays in view while the fields scroll under it. */}
          <Box className={styles.previewSticky} paddingBottom="8">
            <BannerPreview
              banner={draft}
              imageSrc={imageSlotSrc(draft.image)}
              darkImageSrc={imageSlotSrc(draft.dark_image)}
            />
          </Box>

          <div className={styles.formColumns}>
            <Stack gap="32">
              <Section title="Copy">
                <BannerCopyFields
                  draft={draft}
                  errors={shownErrors}
                  onChange={(field, value) => update({[field]: value})}
                />
              </Section>

              <Section title="Audience">
                <Split gap="8" alignY="center">
                  <Split.Item>
                    <Checkbox
                      id="banner-fte-only"
                      checked={draft.fte_only}
                      onChange={(e) => update({fte_only: e.target.checked})}
                    />
                  </Split.Item>
                  <Split.Item>
                    <FormLabel htmlFor="banner-fte-only">
                      Full-time employees only (hide from contractors)
                    </FormLabel>
                  </Split.Item>
                </Split>
              </Section>

              <Section title="Schedule">
                <BannerScheduleField
                  startsAt={draft.starts_at}
                  endsAt={draft.ends_at}
                  useLocalTime={draft.use_local_time}
                  error={shownErrors.schedule}
                  onChange={update}
                />
              </Section>
            </Stack>

            <Stack gap="32">
              <Section title="Images">
                <Stack gap="12">
                  <BannerImagePicker
                    id="banner-image"
                    label="Light mode image"
                    helperText={`At least ${MIN_IMAGE_SIZE.width}×${MIN_IMAGE_SIZE.height}; ${SHARPEST_IMAGE_SIZE.width}×${SHARPEST_IMAGE_SIZE.height} (16:9) or larger looks sharpest. It's cropped to fill the image card.`}
                    slot={draft.image}
                    onChange={(slot) => setImage('image', slot)}
                  />
                  <BannerImagePicker
                    id="banner-dark-image"
                    label="Dark mode image"
                    helperText="Optional. Without one, dark mode uses the light mode image."
                    slot={draft.dark_image}
                    onChange={(slot) => setImage('dark_image', slot)}
                  />
                  {shownErrors.images && (
                    <Text size="small" color="error" role="alert">
                      {shownErrors.images}
                    </Text>
                  )}
                </Stack>
              </Section>

              <Section title="Colors">
                <BannerColorsField
                  colors={draft}
                  palette={palette}
                  darkPalette={darkPalette}
                  // Each color field shows its own format error.
                  onChange={update}
                />
              </Section>
            </Stack>
          </div>

          {save.isError && (
            <SystemBanner tone="alert" role="alert">
              <Text>{saveErrorMessage(save.error)}</Text>
            </SystemBanner>
          )}
          {showErrors && Object.keys(errors).length > 0 && (
            <SystemBanner tone="alert" role="alert">
              <Text>Fix the highlighted fields to save.</Text>
            </SystemBanner>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={onClose} disabled={save.isPending}>
          Cancel
        </CancelButton>
        <Button variant="primary" onClick={submit} isLoading={save.isPending}>
          {banner ? 'Save changes' : 'Create banner'}
        </Button>
      </Modal.Footer>
    </>
  );
};
